import type { VscProductV2 } from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const REQUIRED_MESSAGE = "This is a required field.";

/** Sentinel `termMonths` takes when "Unlimited Months?" is checked — real value, never shown to the user. */
export const UNLIMITED_MONTH_SENTINEL = "999";
/** Sentinel `termMiles` takes when "Unlimited Miles?" is checked — real value, never shown to the user. */
export const UNLIMITED_MILE_SENTINEL = "999999";

/** The "Select an Option" entry is itself a real, selectable list item (not a `SelectValue` placeholder) — matches the legacy dropdown's actual shape. Label is translated in the component, alongside the other Form Number options. */
export const FORM_NUMBER_SELECT_AN_OPTION = "__select_an_option__";

// ---------------------------------------------------------------------------
// vehicleServiceContractInformationSchema — RHF + Zod for
// VehicleServiceContractInformation.tsx. Values come from the BFF's
// `additionalProducts.vsc` (`VscProductV2`); the TPAP-vs-non-TPAP split comes
// from `additionalProducts.metadata.tpapVsc`.
//
// Deliberately NOT modeled (no equivalent concept exists anywhere in this
// codebase): the "match original value on file" checks for Selling Price/Term
// Months/Term Miles/Effective Date (needs a correction-letter feature flag) and
// the AH-deal-status special certificate requirement.
// ---------------------------------------------------------------------------
export const vehicleServiceContractInformationSchema = z
  .object({
    // Never user-editable and never validated — just reflects whatever drives
    // the TPAP-vs-non-TPAP branching below (see the superRefine).
    tpapVsc: z.boolean(),
    // Non-TPAP pure display counterpart to `tpapVendor` — never validated.
    vendorDisplay: z.string(),
    // The selected vendor's id as text. "Not directly enforced by required-field validation" per spec.
    tpapVendor: z.string(),
    certificateNumber: z.string(),
    // RHF needs null for a cleared controlled field; undefined restores its default.
    sellingPrice: z.number().nullable(),
    // Non-TPAP pure display counterpart to Term Months/Miles — never validated.
    termDisplay: z.string(),
    isUnlimitedMonth: z.boolean(),
    termMonths: z.string(),
    isUnlimitedMile: z.boolean(),
    termMiles: z.string(),
    effectiveDate: z.string(),
    formNumber: z.string(),
  })
  .superRefine((data, context) => {
    if (!data.tpapVsc) {
      return;
    }
    if (!data.certificateNumber) {
      context.addIssue({ code: "custom", path: ["certificateNumber"], message: REQUIRED_MESSAGE });
    }
    if (data.sellingPrice === null) {
      context.addIssue({ code: "custom", path: ["sellingPrice"], message: REQUIRED_MESSAGE });
    }
    if (!data.isUnlimitedMonth && !data.termMonths) {
      context.addIssue({ code: "custom", path: ["termMonths"], message: REQUIRED_MESSAGE });
    }
    if (!data.isUnlimitedMile && !data.termMiles) {
      context.addIssue({ code: "custom", path: ["termMiles"], message: REQUIRED_MESSAGE });
    }
    if (!data.effectiveDate) {
      context.addIssue({ code: "custom", path: ["effectiveDate"], message: REQUIRED_MESSAGE });
    }
    if (!data.formNumber || data.formNumber === FORM_NUMBER_SELECT_AN_OPTION) {
      context.addIssue({ code: "custom", path: ["formNumber"], message: REQUIRED_MESSAGE });
    }
  });

export type VehicleServiceContractInformationInput = z.infer<typeof vehicleServiceContractInformationSchema>;

/**
 * Builds RHF defaultValues from the BFF's `additionalProducts.vsc`. Unlimited
 * term flags collapse into the legacy sentinels so the form's checkbox/term
 * coupling stays unchanged.
 * @param vsc The dashboard response's VSC product, if any.
 * @param tpapVsc Whether the VSC is a third-party (TPAP) product — `additionalProducts.metadata.tpapVsc`.
 * @returns {VehicleServiceContractInformationInput} Seed values for `useForm`'s `defaultValues`.
 */
export function buildVscDefaultValues(vsc?: VscProductV2, tpapVsc = false): VehicleServiceContractInformationInput {
  const isUnlimitedMonth = vsc?.unlimitedMonths ?? false;
  const isUnlimitedMile = vsc?.unlimitedMileage ?? false;
  const termMonths = isUnlimitedMonth ? UNLIMITED_MONTH_SENTINEL : toTermText(vsc?.termMonths);
  const termMiles = isUnlimitedMile ? UNLIMITED_MILE_SENTINEL : toTermText(vsc?.termMiles);
  return {
    tpapVsc,
    vendorDisplay: vsc?.vendorName ?? "",
    tpapVendor: typeof vsc?.vendorId === "number" ? String(vsc.vendorId) : "",
    certificateNumber: vsc?.certificateNo ?? "",
    // eslint-disable-next-line unicorn/no-null -- RHF's controlled empty value
    sellingPrice: vsc?.sellingPrice ?? null,
    termDisplay: [formatTerm(isUnlimitedMonth, vsc?.termMonths), formatTerm(isUnlimitedMile, vsc?.termMiles)]
      .filter(Boolean)
      .join(" / "),
    isUnlimitedMonth,
    termMonths,
    isUnlimitedMile,
    termMiles,
    effectiveDate: vsc?.effectiveDate ?? "",
    formNumber: vsc?.formNo ?? FORM_NUMBER_SELECT_AN_OPTION,
  };
}

/**
 * Maps the form back to the save request's VSC node (`vsc` is written in full).
 * Sends the chosen vendor's id; the original name is kept only while the vendor is unchanged.
 * @param values Current form values.
 * @param original The dashboard's VSC product the form was seeded from.
 * @returns {VscProductV2} The VSC product to save.
 */
export function toVscSaveRequest(
  values: VehicleServiceContractInformationInput,
  original?: VscProductV2
): VscProductV2 {
  const hasForm = values.formNumber && values.formNumber !== FORM_NUMBER_SELECT_AN_OPTION;
  const vendorId = values.tpapVendor ? Number(values.tpapVendor) : (original?.vendorId ?? undefined);
  return {
    vendorId,
    vendorName: vendorId === original?.vendorId ? original?.vendorName || undefined : undefined,
    certificateNo: values.certificateNumber || undefined,
    effectiveDate: values.effectiveDate || undefined,
    sellingPrice: values.sellingPrice ?? undefined,
    termMonths: toTermNumber(values.termMonths),
    termMiles: toTermNumber(values.termMiles),
    unlimitedMonths: values.isUnlimitedMonth,
    unlimitedMileage: values.isUnlimitedMile,
    formNo: hasForm ? values.formNumber : undefined,
  };
}

/**
 * Renders a stored term as form text.
 * @param {number | null} [value] - The term in months or miles.
 * @returns {string} The term as text, or empty when absent.
 */
function toTermText(value?: number | null): string {
  return typeof value === "number" ? String(value) : "";
}

/**
 * Parses form text back to a term.
 * @param {string} value - The term text.
 * @returns {number | undefined} The term, or undefined when blank.
 */
function toTermNumber(value: string): number | undefined {
  return value.trim() === "" ? undefined : Number(value);
}

/**
 * Renders a term for read-only display.
 * @param {boolean} isUnlimited - Whether the term is unlimited.
 * @param {number | null} [value] - The term in months or miles.
 * @returns {string} "Unlimited", the term as text, or empty when absent.
 */
function formatTerm(isUnlimited: boolean, value?: number | null): string {
  if (isUnlimited) {
    // eslint-disable-next-line i18n-text/no-en -- read-only display text for the non-TPAP Term field
    return "Unlimited";
  }
  return typeof value === "number" ? String(value) : "";
}
